/**
 * Component-level sized tokens from Figma (Semantic Sizes), transposed from
 * Figma's mode-first indexing (`Normal/Button/height`) to component-first
 * indexing so each component can independently pick a size variant.
 * Exposed on the theme as `theme.componentSizes.button.normal.height`.
 *
 * Page-level layout sizes live in `semantic-sizes.ts`.
 *
 * All values are raw numbers (px). Font-size–like fields (`fontSize`,
 * `iconSize`, `titleSize`, `number`, `unit`) are resolved at theme
 * construction time via `buildComponentSizes(typography)` since typography
 * is a theme-level property. All other values are static.
 */
import type { SemanticTypography } from './semantic-typography';
import type { Sizes } from '../types';
import { primitiveSizes, spacingRaw } from './sizes';

export interface CardSize {
    padding: number;
    radius: number;
    fontSize: number;
}

export interface ButtonSize {
    attentionText: number;
    horizontalPadding: number;
    verticalPadding: number;
    radius: number;
    spaceBetween: number;
    height: number;
    fontSize: number;
    iconSize: number;
    border: number;
}

export interface ErrorMessageSize {
    padding: number;
}

export interface IconButtonSize {
    padding: number;
    radius: number;
    height: number;
    iconSize: number;
}

export interface InputFieldSize {
    horizontalPadding: number;
    verticalPadding: number;
    radius: number;
    spaceBetween: number;
    height: number;
    attentionText: number;
}

export interface MenuItemSize {
    betweenPadding: number;
    sidePadding: number;
    topBottomPadding: number;
    radius: number;
    height: number;
}

export interface DropdownSize {
    betweenPadding: number;
    horizontalPadding: number;
    verticalPadding: number;
    radius: number;
    height: number;
}

export interface ConnectorSize {
    height: number;
}

export interface ContextualMenuSize {
    height: number;
    radius: number;
}

export interface P3PillSize {
    height: number;
    horizontalPadding: number;
}

export interface P3CardSize {
    padding: number;
    gap: number;
    iconSize: number;
    titleSize: number;
    cornerRadius: number;
    number: number;
    unit: number;
}

export interface P3ChargerSize {
    padding: number;
}

export interface P3ModalSize {
    smallPadding: number;
    sectionPadding: number;
}

export interface P3SidebarSize {
    sectionPaddingBetween: number;
    paddingBetween: number;
    leftPadding: number;
    stepSidePadding: number;
    verticalGap: number;
    verticalPadding: number;
}

export interface P3HeaderSize {
    height: number;
    padding: number;
}

export interface P3LayoutSize {
    sectionPadding: number;
    subsectionPadding: number;
    normalPadding: number;
}

export interface P3Size {
    layout: Record<Sizes, P3LayoutSize>;
    pill: Record<Sizes, P3PillSize>;
    card: Record<Sizes, P3CardSize>;
    charger: Record<Sizes, P3ChargerSize>;
    modal: Record<Sizes, P3ModalSize>;
    sidebar: Record<Sizes, P3SidebarSize>;
    header: Record<Sizes, P3HeaderSize>;
}

export interface TabSize {
    height: number;
    horizontalPadding: number;
    verticalPadding: number;
    spaceBetween: number;
}

export interface ComponentSizes {
    card: Record<Sizes, CardSize>;
    button: Record<Sizes, ButtonSize>;
    errorMessage: Record<Sizes, ErrorMessageSize>;
    iconButton: Record<Sizes, IconButtonSize>;
    inputField: Record<Sizes, InputFieldSize>;
    menuItem: Record<Sizes, MenuItemSize>;
    dropdown: Record<Sizes, DropdownSize>;
    connector: Record<Sizes, ConnectorSize>;
    contextualMenu: Record<Sizes, ContextualMenuSize>;
    p3: P3Size;
    tab: Record<Sizes, TabSize>;
}

/** Figma's `Button/icon-size`: the fixed `Icon/icon-14` and `Icon/icon-12`, not a font size. */
const buttonIconSize = { medium: 14, small: 12 };

export const buildComponentSizes = (typography: SemanticTypography): ComponentSizes => {
    const { fontSize } = typography;
    return {
        card: {
            xSmall: {
                padding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                fontSize: fontSize.h2,
            },
            small: {
                padding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                fontSize: fontSize.h2,
            },
            normal: {
                padding: spacingRaw(6),
                radius: primitiveSizes.radius.standard,
                fontSize: fontSize.h1,
            },
            large: {
                padding: spacingRaw(6),
                radius: primitiveSizes.radius.standard,
                fontSize: fontSize.h1,
            },
        },
        button: {
            xSmall: {
                attentionText: spacingRaw(1),
                horizontalPadding: spacingRaw(2),
                verticalPadding: spacingRaw(2),
                radius: primitiveSizes.radius.small,
                spaceBetween: spacingRaw(1),
                height: spacingRaw(8),
                fontSize: fontSize.description,
                iconSize: buttonIconSize.small,
                border: 1,
            },
            small: {
                attentionText: spacingRaw(1),
                horizontalPadding: spacingRaw(3),
                verticalPadding: spacingRaw(4),
                radius: primitiveSizes.radius.medium,
                spaceBetween: spacingRaw(2),
                height: spacingRaw(10),
                fontSize: fontSize.description,
                iconSize: buttonIconSize.small,
                border: 1,
            },
            normal: {
                attentionText: spacingRaw(1),
                horizontalPadding: spacingRaw(4),
                verticalPadding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                spaceBetween: spacingRaw(2),
                height: spacingRaw(12),
                fontSize: fontSize.body,
                iconSize: buttonIconSize.medium,
                border: 1,
            },
            large: {
                attentionText: spacingRaw(1),
                horizontalPadding: spacingRaw(6),
                verticalPadding: spacingRaw(5),
                radius: primitiveSizes.radius.standard,
                spaceBetween: spacingRaw(2),
                height: spacingRaw(14),
                fontSize: fontSize.body,
                iconSize: buttonIconSize.medium,
                border: 1,
            },
        },
        errorMessage: {
            xSmall: { padding: spacingRaw(4) },
            small: { padding: spacingRaw(4) },
            normal: { padding: spacingRaw(4) },
            large: { padding: spacingRaw(4) },
        },
        iconButton: {
            xSmall: {
                padding: spacingRaw(1),
                radius: 100,
                height: spacingRaw(8),
                iconSize: spacingRaw(3),
            },
            small: {
                padding: spacingRaw(2),
                radius: 100,
                height: spacingRaw(10),
                iconSize: spacingRaw(3),
            },
            normal: {
                padding: spacingRaw(4),
                radius: 100,
                height: spacingRaw(12),
                iconSize: spacingRaw(4),
            },
            large: {
                padding: spacingRaw(4),
                radius: 100,
                height: spacingRaw(14),
                iconSize: spacingRaw(5),
            },
        },
        inputField: {
            xSmall: {
                horizontalPadding: spacingRaw(3),
                /* Figma sets this to spacing-3 (12px), but a 40px-tall input
                   with 12px top/bottom padding leaves only 16px of content
                   area — not enough for the floating label + value stack.
                   Figma's preview only looks right because the canvas
                   doesn't clip overflow; in the browser the stack gets cut
                   off at the container edge. Tighten the vertical padding
                   here so the layout actually fits. */
                verticalPadding: spacingRaw(1),
                radius: primitiveSizes.radius.standard,
                spaceBetween: spacingRaw(2),
                height: spacingRaw(10),
                attentionText: spacingRaw(1),
            },
            small: {
                horizontalPadding: spacingRaw(4),
                verticalPadding: spacingRaw(1),
                radius: primitiveSizes.radius.standard,
                spaceBetween: spacingRaw(2),
                height: spacingRaw(12),
                attentionText: spacingRaw(1),
            },
            normal: {
                horizontalPadding: spacingRaw(4),
                verticalPadding: spacingRaw(2),
                radius: primitiveSizes.radius.standard,
                spaceBetween: spacingRaw(3),
                height: spacingRaw(14),
                attentionText: spacingRaw(1),
            },
            large: {
                horizontalPadding: spacingRaw(4),
                verticalPadding: spacingRaw(3),
                radius: primitiveSizes.radius.standard,
                spaceBetween: spacingRaw(2),
                height: spacingRaw(16),
                attentionText: spacingRaw(1),
            },
        },
        menuItem: {
            xSmall: {
                betweenPadding: spacingRaw(3),
                sidePadding: spacingRaw(4),
                topBottomPadding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(10),
            },
            small: {
                betweenPadding: spacingRaw(3),
                sidePadding: spacingRaw(4),
                topBottomPadding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(12),
            },
            normal: {
                betweenPadding: spacingRaw(3),
                sidePadding: spacingRaw(4),
                topBottomPadding: spacingRaw(3),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(14),
            },
            large: {
                betweenPadding: spacingRaw(3),
                sidePadding: spacingRaw(4),
                topBottomPadding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(19),
            },
        },
        dropdown: {
            xSmall: {
                betweenPadding: spacingRaw(3),
                horizontalPadding: spacingRaw(2),
                verticalPadding: spacingRaw(2),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(10),
            },
            small: {
                betweenPadding: spacingRaw(3),
                horizontalPadding: spacingRaw(3),
                verticalPadding: spacingRaw(3),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(12),
            },
            normal: {
                betweenPadding: spacingRaw(3),
                horizontalPadding: spacingRaw(4),
                verticalPadding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(14),
            },
            large: {
                betweenPadding: spacingRaw(3),
                horizontalPadding: spacingRaw(4),
                verticalPadding: spacingRaw(4),
                radius: primitiveSizes.radius.standard,
                height: spacingRaw(16),
            },
        },
        connector: {
            xSmall: { height: spacingRaw(15) },
            small: { height: spacingRaw(15) },
            normal: { height: spacingRaw(15) },
            large: { height: spacingRaw(19) },
        },
        contextualMenu: {
            xSmall: { height: spacingRaw(12), radius: primitiveSizes.radius.standard },
            small: { height: spacingRaw(12), radius: primitiveSizes.radius.standard },
            normal: { height: spacingRaw(12), radius: primitiveSizes.radius.standard },
            large: { height: spacingRaw(12), radius: primitiveSizes.radius.standard },
        },
        p3: {
            layout: {
                xSmall: {
                    sectionPadding: spacingRaw(3),
                    subsectionPadding: spacingRaw(12),
                    normalPadding: spacingRaw(6),
                },
                small: {
                    sectionPadding: spacingRaw(4),
                    subsectionPadding: spacingRaw(12),
                    normalPadding: spacingRaw(6),
                },
                normal: {
                    sectionPadding: spacingRaw(5),
                    subsectionPadding: spacingRaw(12),
                    normalPadding: spacingRaw(6),
                },
                large: {
                    sectionPadding: spacingRaw(6),
                    subsectionPadding: spacingRaw(12),
                    normalPadding: spacingRaw(6),
                },
            },
            pill: {
                xSmall: { height: spacingRaw(4), horizontalPadding: spacingRaw(1) },
                small: { height: spacingRaw(5), horizontalPadding: spacingRaw(2) },
                normal: { height: spacingRaw(6), horizontalPadding: spacingRaw(2) },
                large: { height: spacingRaw(8), horizontalPadding: spacingRaw(3) },
            },
            card: {
                xSmall: {
                    padding: spacingRaw(2),
                    gap: spacingRaw(2),
                    iconSize: spacingRaw(6),
                    titleSize: fontSize.descriptionSmall,
                    cornerRadius: primitiveSizes.radius.small,
                    number: fontSize.h2,
                    unit: fontSize.descriptionSmall,
                },
                small: {
                    padding: spacingRaw(5),
                    gap: spacingRaw(3),
                    iconSize: spacingRaw(6),
                    titleSize: fontSize.description,
                    cornerRadius: primitiveSizes.radius.medium,
                    number: fontSize.h2,
                    unit: fontSize.description,
                },
                normal: {
                    padding: spacingRaw(6),
                    gap: spacingRaw(4),
                    iconSize: spacingRaw(8),
                    titleSize: fontSize.h4,
                    cornerRadius: primitiveSizes.radius.standard,
                    number: fontSize.h1,
                    unit: fontSize.body,
                },
                large: {
                    padding: spacingRaw(6),
                    gap: spacingRaw(6),
                    iconSize: spacingRaw(10),
                    titleSize: fontSize.h4,
                    cornerRadius: primitiveSizes.radius.standard,
                    number: fontSize.headline,
                    unit: fontSize.body,
                },
            },
            charger: {
                xSmall: { padding: spacingRaw(4) },
                small: { padding: spacingRaw(4) },
                normal: { padding: spacingRaw(4) },
                large: { padding: spacingRaw(4) },
            },
            modal: {
                xSmall: { smallPadding: spacingRaw(2), sectionPadding: spacingRaw(4) },
                small: { smallPadding: spacingRaw(2), sectionPadding: spacingRaw(4) },
                normal: { smallPadding: spacingRaw(2), sectionPadding: spacingRaw(4) },
                large: { smallPadding: spacingRaw(2), sectionPadding: spacingRaw(4) },
            },
            sidebar: {
                xSmall: {
                    sectionPaddingBetween: spacingRaw(20),
                    paddingBetween: spacingRaw(6),
                    leftPadding: spacingRaw(4),
                    stepSidePadding: spacingRaw(4),
                    verticalGap: spacingRaw(6),
                    verticalPadding: spacingRaw(1),
                },
                small: {
                    sectionPaddingBetween: spacingRaw(20),
                    paddingBetween: spacingRaw(2),
                    leftPadding: spacingRaw(3),
                    stepSidePadding: spacingRaw(3),
                    verticalGap: spacingRaw(6),
                    verticalPadding: spacingRaw(1),
                },
                normal: {
                    sectionPaddingBetween: spacingRaw(20),
                    paddingBetween: spacingRaw(2),
                    leftPadding: spacingRaw(3),
                    stepSidePadding: spacingRaw(3),
                    verticalGap: spacingRaw(6),
                    verticalPadding: spacingRaw(1),
                },
                large: {
                    sectionPaddingBetween: spacingRaw(20),
                    paddingBetween: spacingRaw(2),
                    leftPadding: spacingRaw(3),
                    stepSidePadding: spacingRaw(3),
                    verticalGap: spacingRaw(6),
                    verticalPadding: spacingRaw(1),
                },
            },
            header: {
                xSmall: { height: spacingRaw(15), padding: spacingRaw(3) },
                small: { height: spacingRaw(15), padding: spacingRaw(3) },
                normal: { height: spacingRaw(15), padding: spacingRaw(3) },
                large: { height: spacingRaw(15), padding: spacingRaw(3) },
            },
        },
        tab: {
            xSmall: {
                height: spacingRaw(8),
                horizontalPadding: spacingRaw(2),
                verticalPadding: spacingRaw(1),
                spaceBetween: spacingRaw(2),
            },
            small: {
                height: spacingRaw(12),
                horizontalPadding: spacingRaw(3),
                verticalPadding: spacingRaw(2),
                spaceBetween: spacingRaw(2),
            },
            normal: {
                height: spacingRaw(14),
                horizontalPadding: spacingRaw(4),
                verticalPadding: spacingRaw(3),
                spaceBetween: spacingRaw(2),
            },
            large: {
                height: spacingRaw(16),
                horizontalPadding: spacingRaw(5),
                verticalPadding: spacingRaw(4),
                spaceBetween: spacingRaw(2),
            },
        },
    };
};
